import { useId, useRef, useState } from "react";

import IconMinus from "@assets/svg/IconMinus";
import IconPlus from "@assets/svg/IconPlus";
import { useTrack } from "@/analytics/events";
import ScrollReveal from "@/blueprint/components/ScrollReveal/ScrollReveal";
import Section from "@/blueprint/components/Section/Section";
import Text from "@/blueprint/components/Text/Text";
import { HSA_FSA_FAQS } from "@/views/hsa-fsa/hsaFsaContent";
import { cn } from "@/lib/utils";

/* Deliberate departures from Figma: mobile uses the shop FAQ's sizing so the
   two accordions match, and rows sit flush without Figma's 12px gap. */

type FaqItem = (typeof HSA_FSA_FAQS.items)[number];

const FaqRow = ({
  item,
  open,
  onToggle,
}: {
  item: FaqItem;
  open: boolean;
  onToggle: () => void;
}) => {
  const answerId = useId();
  const Icon = open ? IconMinus : IconPlus;

  return (
    <li className="flex flex-col border-t border-stroke">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={answerId}
        onClick={onToggle}
        className="flex w-full items-center gap-xxl py-l text-left tab:items-start"
      >
        <Text
          variant="paragraph-md"
          as="span"
          className="min-w-0 flex-1 tab:text-hsa-faq-question"
        >
          {item.question}
        </Text>
        <Icon className="size-xl shrink-0 text-content-primary" />
      </button>
      <div
        id={answerId}
        role="region"
        aria-label={item.question}
        aria-hidden={!open}
        inert={!open}
        className={cn(
          "grid transition-[grid-template-rows] duration-medium ease-in-out-heavy motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="pb-l pr-4xl tab:pr-0">
            <Text
              variant="paragraph-sm"
              tone="secondary"
              className="whitespace-pre-line tab:text-hsa-faq-question"
            >
              {item.answer}
            </Text>
            {"bullets" in item && (
              <ul className="mt-[8px] list-disc pl-[20px]">
                {item.bullets.map((bullet) => (
                  <li key={bullet}>
                    <Text
                      variant="paragraph-sm"
                      as="span"
                      tone="secondary"
                      className="tab:text-hsa-faq-question"
                    >
                      {bullet}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

const HsaFsaFaqs = ({ position }: { position: number }) => {
  const [openId, setOpenId] = useState<FaqItem["id"] | null>(null);
  /* Mirrors openId synchronously: two clicks in one tick would otherwise both
     read the pre-render value and report two opens. */
  const openIdRef = useRef<FaqItem["id"] | null>(null);
  const track = useTrack();

  return (
    <Section
      id="hsa-fsa-faqs"
      position={position}
      elementId="faq"
      ariaLabelledBy="hsa-fsa-faqs-heading"
      className="flex flex-col items-center gap-[40px] bg-brand-grey px-grid-margin py-[72px] tab:gap-[56px] tab:py-[96px] sd:gap-[64px] sd:py-[128px] d:gap-[80px]"
    >
      <ScrollReveal className="flex w-full flex-col items-center gap-[12px] text-center tab:gap-[16px] sd:gap-[20px] d:gap-[24px]">
        <Text variant="h3" as="h2">
          <span id="hsa-fsa-faqs-heading">{HSA_FSA_FAQS.headline}</span>
        </Text>
        <Text variant="paragraph-lg" tone="secondary">
          {HSA_FSA_FAQS.subheadLead}
          <a
            href={`mailto:${HSA_FSA_FAQS.subheadEmail}`}
            className="font-semibold underline-offset-2 hover:underline"
          >
            {HSA_FSA_FAQS.subheadEmail}
          </a>
        </Text>
      </ScrollReveal>

      <ScrollReveal className="w-full d:w-[1042px]" delayMs={80}>
        <ul className="flex w-full flex-col">
          {HSA_FSA_FAQS.items.map((item) => (
            <FaqRow
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => {
                const isOpening = openIdRef.current !== item.id;
                openIdRef.current = isOpening ? item.id : null;
                setOpenId(openIdRef.current);
                if (isOpening) {
                  track("hsa_fsa_faq_opened", { question_id: item.id });
                }
              }}
            />
          ))}
        </ul>
      </ScrollReveal>
    </Section>
  );
};

export default HsaFsaFaqs;
